import { DEVIS_STYLES } from '../../constants/documents';
import { validiteParDefaut } from '../../lib/echeances';
import { trf } from '../../lib/i18n';
import { tauxTvaParDefaut } from '../../lib/tva';
import { today } from '../../lib/utils';
import { DA, DOC_COULEURS } from '../../theme/palette';

/** Les couleurs proposées pour les devis et factures, des plus sombres aux plus claires. */
export const COULEURS_DOCUMENT = [
  '#2A2A2A',
  '#475569',
  '#1E3A5F',
  '#1D4ED8',
  '#5B9BD5',
  '#0E7490',
  '#0F6B3A',
  '#6B7F2A',
  '#B45309',
  '#E36414',
  '#C0392B',
  '#7F1D1D',
  '#BE3A7A',
  '#6D28D9',
  '#7A4E2D',
  '#C6CBD3',
];

/**
 * @param {string|null|undefined} docCouleur couleur enregistrée : code « #… » ou
 *   ancien nom de couleur (« plomberie », « neutre »…)
 * @param {string} style modèle du document
 * @returns {string} la couleur d'accent qui s'imprime sur le document
 */
export function accentDuDocument(docCouleur, style) {
  if (docCouleur && docCouleur[0] === '#') return docCouleur;
  // Sans couleur choisie, les offres et factures s'impriment en gris clair
  // (« neutre ») : l'aperçu montre la même chose.
  const id = docCouleur || 'neutre';
  const cur = DEVIS_STYLES.find((x) => x.id === style) || DEVIS_STYLES[0];
  return DOC_COULEURS.find((c) => c.id === id)?.accent || cur.accent || DA;
}

/**
 * L'offre d'exemple montrée dans Réglages, avec les coordonnées de la société.
 *
 * @param {object} soc la société
 * @param {boolean} estMag offre du magasin (articles) plutôt que du bâtiment
 * @param {string} style modèle du document
 * @param {string|null|undefined} docCouleur couleur du document
 * @returns {{client: object, doc: object}} le client et l'offre de l'aperçu
 */
export function offreDExemple(soc, estMag, style, docCouleur) {
  const cur = DEVIS_STYLES.find((x) => x.id === style) || DEVIS_STYLES[0];
  const client = { nom: 'Marie Dupont', adr: 'Chemin des Fleurs 12', npa: '1004 Lausanne' };
  const doc = {
    kind: 'devis',
    estMag,
    sect: estMag ? 'magasin' : 'batiment',
    accent: accentDuDocument(docCouleur, cur.id),
    style: cur.id,
    num: 'OFF-2026-001',
    date: today(),
    // Une date, comme sur l'offre : « Valable jusqu'au 30 jours »
    // ne voulait rien dire. La validité et l'acompte sont ceux
    // réglés dans Réglages.
    ech: (() => {
      const d = new Date();
      d.setDate(d.getDate() + validiteParDefaut(soc));
      return (
        String(d.getDate()).padStart(2, '0') +
        '.' +
        String(d.getMonth() + 1).padStart(2, '0') +
        '.' +
        d.getFullYear()
      );
    })(),
    // Un magasin vend des articles : l'aperçu parlait de chantier.
    objet: estMag ? 'Exemple — fourniture de carrelage' : 'Exemple — rénovation salle de bains',
    intro: estMag
      ? 'Madame, Monsieur, nous avons le plaisir de vous soumettre notre offre pour les articles ci-dessous.'
      : 'Madame, Monsieur, nous avons le plaisir de vous soumettre notre offre pour les travaux décrits ci-dessous.',
    mentions: trf('Offre valable {0} jours.', validiteParDefaut(soc)),
    chLieu: '',
    lignes: estMag
      ? [
          { d: 'Carrelage 30x60', q: 40, u: 'm2', p: 42, rem: 0 },
          { d: 'Colle souple', q: 6, u: 'sac', p: 28, rem: 0 },
          { d: 'Livraison', q: 1, u: 'forfait', p: 80, rem: 0 },
        ]
      : [
          { d: "Prestation / main-d'œuvre", q: 10, u: 'h', p: 90, rem: 0 },
          { d: 'Fourniture / matériel', q: 1, u: 'forfait', p: 400, rem: 0 },
          { d: 'Déplacement', q: 1, u: 'forfait', p: 80, rem: 0 },
        ],
    remisePct: 0,
    tvaPct: tauxTvaParDefaut(soc),
    acomptePct: soc?.acompteDefaut ?? 40,
    acompteDelai: 10,
    recu: 0,
  };
  return { client, doc };
}
